import React, { useEffect, useState } from "react";
import axios from "axios";
import { message } from "antd";

const HOMESTAY_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/homestay";
const ROOM_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/homestay-rooms";

const getToken = () => localStorage.getItem("vendorToken") || localStorage.getItem("token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const ROOM_TYPES = [
  ["private-room", "Private Room"], ["deluxe", "Deluxe Room"], ["suite", "Suite"],
  ["family", "Family Room"], ["dormitory", "Dormitory"], ["entire-unit", "Entire Unit"],
];
const BED_TYPES = [["single", "Single"], ["double", "Double"], ["queen", "Queen"], ["king", "King"], ["twin", "Twin"], ["bunk", "Bunk"]];
const MEAL_PLANS = [
  ["none", "No Meals"], ["breakfast", "Breakfast Included"], ["half-board", "Half Board"],
  ["full-board", "Full Board"], ["all-inclusive", "All Inclusive"],
];
const ROOM_AMENITIES = ["AC", "Heater", "TV", "WiFi", "Attached Bathroom", "Geyser", "Balcony", "Mountain View", "Garden View", "Wardrobe", "Work Desk", "Tea/Coffee Maker", "Mini Fridge", "Iron", "Hair Dryer", "Extra Bed Available"];

const DEFAULT_ROOM = {
  roomName: "", roomType: "private-room", description: "", totalRooms: "1",
  maxGuests: "2", maxAdults: "2", maxChildren: "0",
  bedType: "double", totalBeds: "1", bathrooms: "1", roomSize: "", view: "", mealPlan: "none",
  basePrice: "", weekendPrice: "", extraGuestPrice: "", extraBedPrice: "",
};

const label = (list, v) => list.find(([k]) => k === v)?.[1] || v;
const statusOf = (h) => (!h.isActive ? "inactive" : String(h.status || "DRAFT").toLowerCase());
const STATUS_STYLE = {
  draft: ["#2563eb", "#dbeafe"], pending: ["#d97706", "#fef3c7"], approved: ["#16a34a", "#dcfce7"],
  rejected: ["#dc2626", "#fee2e2"], suspended: ["#dc2626", "#fee2e2"], inactive: ["#6b7280", "#f3f4f6"],
};
const to12 = (t) => t || "—";

export default function VendorHomestayRooms() {
  const [view, setView] = useState("select"); // select | rooms | form
  const [homestays, setHomestays] = useState([]);
  const [homestay, setHomestay] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [galleryIdx, setGalleryIdx] = useState(0);

  const [form, setForm] = useState(DEFAULT_ROOM);
  const [amenities, setAmenities] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [editId, setEditId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!getToken()) { window.location.href = "/vendor-login"; return; }
    fetchHomestays();
  }, []);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };
  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const fetchHomestays = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${HOMESTAY_API}/my?limit=100`, authHeaders());
      setHomestays(res.data.homestays || []);
    } catch (e) { setError(e.response?.data?.message || "Homestays load nahi hue"); }
    setLoading(false);
  };

  const fetchRooms = async (homestayId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${ROOM_API}?homestay=${homestayId}`, authHeaders());
      setRooms(res.data.rooms || []);
    } catch (e) { setError(e.response?.data?.message || "Rooms load nahi hue"); }
    setLoading(false);
  };

  const selectHomestay = (h) => {
    setHomestay(h); setGalleryIdx(0); setError("");
    setView("rooms");
    fetchRooms(h._id);
    window.scrollTo({ top: 0 });
  };

  const changeHomestay = () => { setHomestay(null); setRooms([]); setView("select"); fetchHomestays(); };

  /* ---------- room form ---------- */
  const resetForm = () => {
    setEditId(null); setForm(DEFAULT_ROOM); setAmenities([]);
    setNewImages([]); setPreviews([]); setExistingImages([]); setError("");
  };
  const openAddRoom = () => { resetForm(); setView("form"); window.scrollTo({ top: 0 }); };
  const closeForm = () => { resetForm(); setView("rooms"); window.scrollTo({ top: 0 }); };

  const startEditRoom = (r) => {
    setEditId(r._id);
    setForm({
      roomName: r.roomName || "", roomType: r.roomType || "private-room", description: r.description || "",
      totalRooms: String(r.totalRooms ?? 1), maxGuests: String(r.maxGuests ?? 2),
      maxAdults: String(r.maxAdults ?? 2), maxChildren: String(r.maxChildren ?? 0),
      bedType: r.bedType || "double", totalBeds: String(r.totalBeds ?? 1), bathrooms: String(r.bathrooms ?? 1),
      roomSize: r.roomSize ? String(r.roomSize) : "", view: r.view || "", mealPlan: r.mealPlan || "none",
      basePrice: r.pricing?.basePrice ?? "", weekendPrice: r.pricing?.weekendPrice || "",
      extraGuestPrice: r.pricing?.extraGuestPrice || "", extraBedPrice: r.pricing?.extraBedPrice || "",
    });
    setAmenities(r.amenities || []);
    setNewImages([]); setPreviews([]); setExistingImages(r.images || []);
    setError(""); setView("form"); window.scrollTo({ top: 0 });
  };

  const toggleAmenity = (a) => setAmenities((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));
  const totalImages = existingImages.length + newImages.length;

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((file) => file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024);
    if (valid.length !== files.length) setError("Sirf image files (max 5MB) allowed hain");
    if (totalImages + valid.length > 10) { setError("Max 10 images per room"); return; }
    setNewImages((p) => [...p, ...valid]);
    setPreviews((p) => [...p, ...valid.map((file) => URL.createObjectURL(file))]);
    e.target.value = "";
  };
  const removeNewImage = (i) => {
    setNewImages((p) => p.filter((_, idx) => idx !== i));
    setPreviews((p) => p.filter((_, idx) => idx !== i));
  };
  const removeExistingImage = async (url) => {
    if (!window.confirm("Ye photo delete karni hai?")) return;
    try {
      await axios.delete(`${ROOM_API}/${editId}/images`, { ...authHeaders(), data: { imageUrl: url } });
      setExistingImages((p) => p.filter((x) => x !== url));
      fetchRooms(homestay._id);
    } catch (e) { message.error(e.response?.data?.message || "Photo delete nahi hui"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.roomName.trim()) { setError("Room name is required"); return; }
    if (!form.basePrice || Number(form.basePrice) <= 0) { setError("Base price is required"); return; }
    setSubmitting(true); setError("");

    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    data.append("amenities", JSON.stringify(amenities));
    if (!editId) data.append("homestay", homestay._id);
    newImages.forEach((img) => data.append("images", img));

    try {
      if (editId) await axios.put(`${ROOM_API}/${editId}`, data, authHeaders());
      else await axios.post(ROOM_API, data, authHeaders());
      flash(editId ? "Room updated!" : "Room added!");
      resetForm(); setView("rooms"); fetchRooms(homestay._id); fetchHomestays();
      window.scrollTo({ top: 0 });
    } catch (err) {
      const msg = err.response?.data?.message || "Operation failed";
      message.error(msg); setError(msg);
    }
    setSubmitting(false);
  };

  const toggleRoom = async (r) => {
    try {
      await axios.patch(`${ROOM_API}/${r._id}/toggle-status`, {}, authHeaders());
      fetchRooms(homestay._id);
    } catch (e) { setError(e.response?.data?.message || "Toggle failed"); }
  };

  const deleteRoom = async (r) => {
    if (!window.confirm(`"${r.roomName}" delete karna hai?`)) return;
    try {
      await axios.delete(`${ROOM_API}/${r._id}`, authHeaders());
      flash("Room deleted.");
      fetchRooms(homestay._id); fetchHomestays();
    } catch (e) { setError(e.response?.data?.message || "Delete failed"); }
  };

  const gallery = homestay ? [homestay.coverImage, ...(homestay.propertyImages || [])].filter(Boolean) : [];
  const typeLabel = homestay ? homestay.propertyType : "";

  return (
    <div style={S.page} className="hr-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .hr-page input:focus, .hr-page select:focus, .hr-page textarea:focus { outline: none; border-color: #166534 !important; box-shadow: 0 0 0 3px rgba(22,101,52,.1) !important; }
        .hr-card:hover { border-color: #166534 !important; transform: translateY(-2px); }
        .hr-chip { cursor: pointer; transition: all .2s; }
        .hr-chip:hover { border-color: #166534 !important; color: #166534 !important; }
        .hr-chip.on { background: #166534 !important; color: white !important; border-color: #166534 !important; }
        .hr-thumb:hover { opacity: .85; }
        @media (max-width: 800px) { .hr-grid2, .hr-grid4, .hr-split { grid-template-columns: 1fr !important; } }
      `}</style>

      {success && <div style={S.toast("green")}>{success}</div>}
      {error && view !== "form" && (
        <div style={S.toast("red")}>{error}
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", marginLeft: 8 }}>✕</button>
        </div>
      )}

      {/* ═══════════ STEP 1: SELECT HOMESTAY ═══════════ */}
      {view === "select" && (
        <>
          <h1 style={S.title}>Units / Rooms</h1>
          <p style={{ ...S.subtitle, marginBottom: 24 }}>Pehle wo homestay select karo jisme rooms add ya manage karne hain.</p>

          {loading ? (
            <div style={S.center}><div style={S.spinner} /></div>
          ) : homestays.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>🏠</div>
              <p style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>Abhi koi homestay nahi hai</p>
              <p style={{ color: "#9ca3af", marginTop: 8 }}>Pehle “Property Details” me homestay add karo, phir yahan rooms add kar sakte ho.</p>
            </div>
          ) : (
            <div style={S.pickGrid}>
              {homestays.map((h) => {
                const img = h.coverImage || h.propertyImages?.[0];
                const st = statusOf(h);
                const [c, bg] = STATUS_STYLE[st] || STATUS_STYLE.inactive;
                return (
                  <button key={h._id} type="button" className="hr-card" onClick={() => selectHomestay(h)} style={S.pickCard}>
                    <div style={S.pickImg}>
                      {img ? <img src={img} alt="" style={S.fill} onError={(e) => (e.target.style.display = "none")} /> : <span style={{ fontSize: 44 }}>🏠</span>}
                      <span style={{ ...S.badge, background: bg, color: c }}>{st}</span>
                    </div>
                    <div style={{ padding: 16, textAlign: "left" }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>{h.propertyName}</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>📍 {h.city}{h.state ? `, ${h.state}` : ""}</div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: h.roomCount ? "#166534" : "#d97706" }}>
                          {h.roomCount ? `${h.roomCount} room type${h.roomCount > 1 ? "s" : ""}` : "No rooms yet"}
                        </span>
                        <span style={{ fontSize: 13, color: "#166534", fontWeight: 600 }}>Select →</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════════ STEP 2: HOMESTAY DETAILS + ROOMS ═══════════ */}
      {view === "rooms" && homestay && (
        <>
          <button type="button" onClick={changeHomestay} style={S.backLink}>← Change homestay</button>
          <div style={{ ...S.header, marginTop: 8 }}>
            <div>
              <h1 style={S.title}>{homestay.propertyName}</h1>
              <p style={S.subtitle}>📍 {homestay.address}, {homestay.city}</p>
            </div>
            <button onClick={openAddRoom} style={S.addBtn}>+ Add Room</button>
          </div>

          {/* Homestay summary */}
          <div className="hr-split" style={S.summary}>
            <div>
              <div style={S.mainPhoto}>
                {gallery[galleryIdx] ? <img src={gallery[galleryIdx]} alt="" style={S.fill} /> : <span style={{ fontSize: 56 }}>🏠</span>}
              </div>
              {gallery.length > 1 && (
                <div style={S.thumbRow}>
                  {gallery.slice(0, 8).map((src, i) => (
                    <img key={src} src={src} alt="" className="hr-thumb" onClick={() => setGalleryIdx(i)}
                      style={{ ...S.thumbImg, outline: i === galleryIdx ? "2px solid #166534" : "none" }} />
                  ))}
                </div>
              )}
            </div>
            <div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <span style={S.typeBadge}>{typeLabel}</span>
                {(() => { const st = statusOf(homestay); const [c, bg] = STATUS_STYLE[st] || STATUS_STYLE.inactive; return <span style={{ ...S.typeBadge, background: bg, color: c, textTransform: "capitalize" }}>{st}</span>; })()}
              </div>
              <div style={S.detailGrid}>
                <Detail k="Host" v={homestay.hostName} />
                <Detail k="Phone" v={homestay.hostPhone} />
                <Detail k="Max guests" v={homestay.maxGuests} />
                <Detail k="Bedrooms / Baths" v={`${homestay.totalBedrooms ?? 0} / ${homestay.totalBathrooms ?? 0}`} />
                <Detail k="Check-in" v={to12(homestay.checkInTime)} />
                <Detail k="Check-out" v={to12(homestay.checkOutTime)} />
                <Detail k="Cancellation" v={homestay.cancellationPolicy || "—"} />
                <Detail k="Pincode" v={homestay.pincode} />
              </div>
              {homestay.description && <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginTop: 14 }}>{homestay.description.slice(0, 220)}{homestay.description.length > 220 ? "…" : ""}</p>}
              {homestay.amenities?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                  {homestay.amenities.slice(0, 10).map((a) => <span key={a} style={S.pill}>{a}</span>)}
                  {homestay.amenities.length > 10 && <span style={S.pill}>+{homestay.amenities.length - 10}</span>}
                </div>
              )}
            </div>
          </div>

          {/* Rooms list */}
          <h2 style={{ ...S.cardTitle, margin: "28px 0 14px" }}>Rooms ({rooms.length})</h2>
          {loading ? (
            <div style={S.center}><div style={S.spinner} /></div>
          ) : rooms.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🛏️</div>
              <p style={{ fontSize: 17, fontWeight: 600, color: "#374151" }}>Is homestay me abhi koi room nahi hai</p>
              <button onClick={openAddRoom} style={{ ...S.addBtn, marginTop: 16 }}>+ Add first room</button>
            </div>
          ) : (
            <div style={S.roomGrid}>
              {rooms.map((r) => (
                <div key={r._id} style={{ ...S.roomCard, opacity: r.isActive ? 1 : 0.65 }}>
                  <div style={S.roomImg}>
                    {r.images?.[0] ? <img src={r.images[0]} alt="" style={S.fill} onError={(e) => (e.target.style.display = "none")} /> : <span style={{ fontSize: 40 }}>🛏️</span>}
                    <span style={{ ...S.badge, background: r.isActive ? "#dcfce7" : "#f3f4f6", color: r.isActive ? "#16a34a" : "#6b7280" }}>{r.isActive ? "active" : "inactive"}</span>
                  </div>
                  <div style={{ padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>{r.roomName}</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{label(ROOM_TYPES, r.roomType)} · {r.totalRooms} unit{r.totalRooms > 1 ? "s" : ""}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, fontSize: 17, color: "#166534" }}>₹{Number(r.pricing?.basePrice || 0).toLocaleString("en-IN")}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>per night</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
                      👥 {r.maxGuests} guests · 🛏️ {r.totalBeds} {label(BED_TYPES, r.bedType)} · 🚿 {r.bathrooms}
                    </div>
                    {r.amenities?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10 }}>
                        {r.amenities.slice(0, 4).map((a) => <span key={a} style={S.pill}>{a}</span>)}
                        {r.amenities.length > 4 && <span style={S.pill}>+{r.amenities.length - 4}</span>}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button onClick={() => startEditRoom(r)} style={S.smallBtn}>✏️ Edit</button>
                      <button onClick={() => toggleRoom(r)} style={S.smallBtn}>{r.isActive ? "Deactivate" : "Activate"}</button>
                      <button onClick={() => deleteRoom(r)} style={{ ...S.smallBtn, color: "#dc2626", marginLeft: "auto" }}>🗑️</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══════════ STEP 3: ADD / EDIT ROOM (full page) ═══════════ */}
      {view === "form" && homestay && (
        <form onSubmit={handleSubmit}>
          <button type="button" onClick={closeForm} style={S.backLink}>← Back to rooms</button>
          <h1 style={{ ...S.title, marginTop: 8 }}>{editId ? "Edit Room" : "Add Room"}</h1>

          <div style={S.forBar}>
            <div style={S.forThumb}>
              {gallery[0] ? <img src={gallery[0]} alt="" style={S.fill} /> : <span>🏠</span>}
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Room is homestay me add ho raha hai</div>
              <div style={{ fontWeight: 700, color: "#111827" }}>{homestay.propertyName} <span style={{ fontWeight: 400, color: "#6b7280" }}>· {homestay.city}</span></div>
            </div>
          </div>

          {error && <div style={S.errorBar}>{error}</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Card title="Room Details" icon="🛏️">
              <div className="hr-grid2" style={S.grid2}>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Room Name *</Label>
                  <Input value={form.roomName} onChange={(v) => f("roomName", v)} placeholder="e.g., Deluxe Mountain View Room" />
                </div>
                <div>
                  <Label>Room Type</Label>
                  <Select value={form.roomType} onChange={(v) => f("roomType", v)} options={ROOM_TYPES} />
                </div>
                <div>
                  <Label>Number of such rooms</Label>
                  <Input type="number" min="1" value={form.totalRooms} onChange={(v) => f("totalRooms", v)} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Description</Label>
                  <textarea value={form.description} onChange={(e) => f("description", e.target.value)} placeholder="Room ke baare me likho: view, size, special features..." style={{ ...S.input, minHeight: 90, resize: "vertical" }} />
                </div>
              </div>
            </Card>

            <Card title="Capacity & Beds" icon="👥">
              <div className="hr-grid4" style={S.grid4}>
                <div><Label>Max Guests</Label><Input type="number" min="1" value={form.maxGuests} onChange={(v) => f("maxGuests", v)} /></div>
                <div><Label>Max Adults</Label><Input type="number" min="1" value={form.maxAdults} onChange={(v) => f("maxAdults", v)} /></div>
                <div><Label>Max Children</Label><Input type="number" min="0" value={form.maxChildren} onChange={(v) => f("maxChildren", v)} /></div>
                <div><Label>Bathrooms</Label><Input type="number" min="0" value={form.bathrooms} onChange={(v) => f("bathrooms", v)} /></div>
              </div>
              <div className="hr-grid4" style={{ ...S.grid4, marginTop: 16 }}>
                <div><Label>Bed Type</Label><Select value={form.bedType} onChange={(v) => f("bedType", v)} options={BED_TYPES} /></div>
                <div><Label>Number of Beds</Label><Input type="number" min="1" value={form.totalBeds} onChange={(v) => f("totalBeds", v)} /></div>
                <div><Label>Room Size (sq ft)</Label><Input type="number" min="0" value={form.roomSize} onChange={(v) => f("roomSize", v)} placeholder="e.g., 250" /></div>
                <div><Label>View</Label><Input value={form.view} onChange={(v) => f("view", v)} placeholder="e.g., Mountain" /></div>
              </div>
            </Card>

            <Card title="Pricing & Meals" icon="💰">
              <div className="hr-grid2" style={S.grid2}>
                <div><Label>Base Price / Night (₹) *</Label><Input type="number" min="0" value={form.basePrice} onChange={(v) => f("basePrice", v)} placeholder="e.g., 3000" /></div>
                <div><Label>Weekend Price (₹)</Label><Input type="number" min="0" value={form.weekendPrice} onChange={(v) => f("weekendPrice", v)} placeholder="e.g., 4000" /></div>
                <div><Label>Extra Guest Price (₹)</Label><Input type="number" min="0" value={form.extraGuestPrice} onChange={(v) => f("extraGuestPrice", v)} placeholder="e.g., 500" /></div>
                <div><Label>Extra Bed Price (₹)</Label><Input type="number" min="0" value={form.extraBedPrice} onChange={(v) => f("extraBedPrice", v)} placeholder="e.g., 700" /></div>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label>Meal Plan</Label>
                  <Select value={form.mealPlan} onChange={(v) => f("mealPlan", v)} options={MEAL_PLANS} />
                </div>
              </div>
            </Card>

            <Card title="Room Amenities" icon="✨">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {ROOM_AMENITIES.map((a) => (
                  <button key={a} type="button" className={`hr-chip ${amenities.includes(a) ? "on" : ""}`} onClick={() => toggleAmenity(a)} style={S.chip}>{a}</button>
                ))}
              </div>
            </Card>

            <Card title="Room Photos" icon="📸" hint="Max 10 images, 5MB each">
              <div style={S.imgGrid}>
                {existingImages.map((src) => (
                  <div key={src} style={S.previewItem}>
                    <img src={src} alt="" style={S.fill} onError={(e) => (e.target.style.display = "none")} />
                    <button type="button" onClick={() => removeExistingImage(src)} style={S.removeImg}>✕</button>
                  </div>
                ))}
                {previews.map((src, i) => (
                  <div key={src} style={S.previewItem}>
                    <img src={src} alt="" style={S.fill} />
                    <button type="button" onClick={() => removeNewImage(i)} style={S.removeImg}>✕</button>
                  </div>
                ))}
                {totalImages < 10 && (
                  <label style={S.uploadZone}>
                    <span style={{ fontSize: 32, lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: 13, color: "#9ca3af" }}>Add Photos</span>
                    <input type="file" multiple accept="image/*" hidden onChange={handleImages} />
                  </label>
                )}
              </div>
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 14 }}>{totalImages}/10 images</p>
            </Card>
          </div>

          <div style={S.actionBar}>
            <button type="button" onClick={closeForm} style={S.cancelBtn}>Cancel</button>
            <button type="submit" style={{ ...S.submitBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
              {submitting ? "⏳ Saving..." : editId ? "💾 Update Room" : "✓ Add Room"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ── Sub-components ── */
const Card = ({ title, icon, hint, children }) => (
  <section style={S.card}>
    <div style={{ marginBottom: 18 }}>
      <h2 style={S.cardTitle}>{icon} {title}</h2>
      {hint && <p style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>{hint}</p>}
    </div>
    {children}
  </section>
);
const Label = ({ children }) => <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{children}</label>;
const Input = ({ onChange, ...props }) => <input {...props} onChange={(e) => onChange(e.target.value)} style={S.input} />;
const Select = ({ value, onChange, options }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={S.input}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const Detail = ({ k, v }) => (
  <div>
    <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginTop: 2 }}>{v || "—"}</div>
  </div>
);

/* ── Styles ── */
const S = {
  page: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px 40px", fontFamily: "'DM Sans', sans-serif", background: "#f8fafc" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 16 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 700, color: "#111827" },
  subtitle: { fontSize: 14, color: "#9ca3af", marginTop: 6 },
  backLink: { background: "none", border: "none", color: "#166534", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" },
  addBtn: { padding: "12px 24px", background: "#166534", color: "white", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  toast: (c) => ({ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "14px 20px", maxWidth: 420, background: c === "green" ? "#166534" : "#dc2626", color: "white", borderRadius: 10, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,.15)", display: "flex", alignItems: "center", gap: 8 }),
  errorBar: { marginBottom: 16, padding: "12px 16px", background: "#fee2e2", color: "#dc2626", borderRadius: 8, fontSize: 14, fontWeight: 500 },
  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 240 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "spin 1s linear infinite" },
  empty: { textAlign: "center", padding: 60, background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  fill: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  pickGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 },
  pickCard: { background: "white", border: "1.5px solid #e5e7eb", borderRadius: 16, overflow: "hidden", cursor: "pointer", padding: 0, fontFamily: "inherit", transition: "all .2s" },
  pickImg: { height: 170, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" },
  badge: { position: "absolute", top: 10, right: 10, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, textTransform: "capitalize" },

  summary: { display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)", gap: 24, background: "white", borderRadius: 16, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  mainPhoto: { height: 280, background: "#f3f4f6", borderRadius: 12, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  thumbRow: { display: "flex", gap: 8, marginTop: 10, overflowX: "auto", paddingBottom: 4 },
  thumbImg: { width: 64, height: 50, objectFit: "cover", borderRadius: 8, cursor: "pointer", flexShrink: 0, outlineOffset: 2 },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  typeBadge: { fontSize: 12, fontWeight: 600, color: "#166534", background: "#dcfce7", padding: "4px 12px", borderRadius: 20, textTransform: "capitalize" },
  pill: { fontSize: 11, background: "#f3f4f6", color: "#6b7280", padding: "3px 9px", borderRadius: 4, fontWeight: 500 },

  roomGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 },
  roomCard: { background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  roomImg: { height: 160, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" },
  smallBtn: { padding: "7px 12px", background: "#f3f4f6", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#374151" },

  forBar: { display: "flex", alignItems: "center", gap: 14, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "12px 16px", margin: "14px 0 20px" },
  forThumb: { width: 56, height: 44, borderRadius: 8, overflow: "hidden", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  card: { background: "white", borderRadius: 16, padding: "24px 28px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  cardTitle: { fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 700, color: "#111827" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  grid4: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 },
  input: { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "white", boxSizing: "border-box", transition: "border .2s, box-shadow .2s" },
  chip: { padding: "8px 16px", border: "1.5px solid #e5e7eb", borderRadius: 20, background: "white", fontSize: 13, fontWeight: 500, color: "#6b7280", fontFamily: "inherit" },
  imgGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, 120px)", gap: 16 },
  previewItem: { width: 120, height: 96, borderRadius: 8, position: "relative" },
  removeImg: { position: "absolute", top: -8, right: -8, background: "#dc2626", color: "white", border: "none", borderRadius: "50%", width: 22, height: 22, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  uploadZone: { width: 120, height: 96, border: "2px dashed #d1d5db", borderRadius: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#9ca3af", gap: 4 },
  actionBar: { position: "sticky", bottom: 0, zIndex: 15, display: "flex", justifyContent: "flex-end", gap: 12, padding: "14px 20px", marginTop: 20, background: "rgba(255,255,255,.96)", border: "1px solid #e5e7eb", borderRadius: 14, boxShadow: "0 -2px 12px rgba(0,0,0,.06)" },
  cancelBtn: { padding: "10px 20px", background: "#f3f4f6", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#374151" },
  submitBtn: { padding: "10px 24px", background: "#166534", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
};